import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Injector, OnDestroy, OnInit } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { NavigationEnd, RouterModule } from '@angular/router';
import { assets } from '../../core/constants/assets.const';
import { SharedModule } from '../../shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CreditBalanceResponse, dashboardNotification, UserResponse } from '../../models/auth.model';
import { BaseClass } from '../../shared/class/baseClass';
import { environment } from '../../../environments/environment';
import { UserRole } from '../../core/constants/keys.constant';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SocketService } from '../../services/socket.service';
import { GuidedTourComponent } from '../../shared/components/guided-tour/guided-tour.component';
import { GuidedTourService } from '../../shared/services/guided-tour.service';
import { ChatbotFabComponent } from '../../pages/after-login-page/web/ai-counselor/components/chatbot-fab/chatbot-fab.component';
import { CdseSfTriggerService } from '../../services/cdse-sf-trigger.service';
import { CopilotPanelComponent } from '../../pages/after-login-page/web/ai-counselor/components/copilot-panel/copilot-panel.component';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { NotificationDrawerComponent } from '../../modals/notification-drawer/notification-drawer.component';
import { PremiumUpgradeWizardComponent } from '../../modals/premium-upgrade-wizard/premium-upgrade-wizard.component';
import { GuideHostComponent } from '../../guides/guide-host.component';

@Component({
  selector: 'mindbase-main',
  standalone: true,
  imports: [MatSidenavModule, MatMenuModule, CommonModule, RouterModule, SharedModule, TranslateModule, MatBadgeModule, MatIconModule, MatTooltipModule, GuidedTourComponent, ChatbotFabComponent, CopilotPanelComponent, BreadcrumbComponent, NotificationDrawerComponent, GuideHostComponent],
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss'
})
export class MainComponent extends BaseClass implements OnInit, AfterViewInit, OnDestroy {
  private destroy: Subject<boolean> = new Subject<boolean>();
  protected subscriptionSubject$!: Subscription;
  protected profile: UserResponse | null = null;
  public assets = assets;
  public sidenavWidth = 200;
  protected url: any;
  protected currentPath: 'tutorCounselor' | 'studentParent' = 'studentParent';
  protected isAiCounselorPage = false;
  protected isCopilotOpen = false;
  /** Gates the chatbot FAB / copilot panel (admin: Permissions → My Counselor → AI Counselor,
   *  or Instructor Permissions → AI Tutor → AI Counselor for tutor/counsellor). */
  protected canAccessAiCounselor = true;
  /** Gates the header level badge, which links to /dashboard/achievements
   *  (admin: Permissions → Pathway Features → AI/Pathway Achievements). */
  protected canAccessAchievements = true;
  /** Gates the header "Upgrade to Premium" button
   *  (admin: Permissions → Pathway Features → Package Bundle). */
  protected canAccessPackageBundle = true;
  protected userRole = UserRole;
  protected webLogo: string | undefined;
  public notificationCount: number | undefined = 0;
  protected isNotificationDrawerOpen = false;
  protected creditBalance: CreditBalanceResponse | null = null;
  /** Desktop sidebar is a 90px icon rail that expands while the pointer is over it (mirrors the admin app). */
  protected isSidebarHovered = false;
  /** Drives the sidebar on small screens, where it stays a tap-to-open overlay (hamburger / nav-item click). */
  private isMobileSidebarOpen = false;
  /** Matches the 1199.98px sidebar breakpoints in `styles.scss`. */
  private static readonly SIDEBAR_HOVER_MIN_WIDTH = 1200;

  constructor(
    injector: Injector,
    private readonly _socketService: SocketService,
    private readonly cdr: ChangeDetectorRef,
    private readonly guidedTourService: GuidedTourService,
    private readonly cdseSfTrigger: CdseSfTriggerService,
    private readonly dialog: MatDialog,
  ) {
    super(injector);
  }

  /** Fires the CDSE-SF eligibility prompt once per shell load, for students only. */
  private cdsePromptDone = false;
  private tryPromptCdse(): void {
    if (this.cdsePromptDone) return;
    if (this.profile?.role === UserRole.STUDENT) {
      this.cdsePromptDone = true;
      this.cdseSfTrigger.reset();
      this.cdseSfTrigger.checkAndPrompt();
    }
  }

  protected restartTour(): void {
    this.guidedTourService.restartTour();
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy)
      )
      .subscribe(() => {
        this.refreshShellForUrl(this.router.url);
        // Runs after each navigation settles — covers login (profile pre-loaded)
        // and refresh; the once-guard keeps it to a single prompt per shell load.
        this.tryPromptCdse();
      });
    this.refreshShellForUrl(this.router.url);

    window.addEventListener('storage', (event: StorageEvent) => {
      if (!event.key) return; // no key, ignore
      // Only reload on a real auth-state boundary (login/logout in another tab) — NOT on every
      // routine access-token rotation (background refresh timer / silent 401 refresh / reload
      // trigger all rewrite this key every few minutes while staying logged in; reloading on
      // every one of those causes a cross-tab reload ping-pong).
      if (event.key === environment.storageKey && event.oldValue !== event.newValue) {
        const becameLoggedOut = !event.newValue;
        const becameLoggedIn = !event.oldValue && !!event.newValue;
        if (becameLoggedOut || becameLoggedIn) {
          window.location.reload();
        }
      }
    });
    this.subscriptionSubject$ = this.authService.profileDetailsSubject
      .pipe(takeUntil(this.destroy))
      .subscribe((res: UserResponse | null) => {
        if (res !== null) {
          if (res.role !== UserRole.ADMIN) {
            this.profile = res;
            const timeZoneToStore = res.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            localStorage.setItem('timezone', timeZoneToStore);
            this.setSw(this.profile.role);
            if (this.profile.role === UserRole.TUTOR || this.profile.role === UserRole.COUNSELOR) {
              this.currentPath = 'tutorCounselor';
            } else {
              this.currentPath = 'studentParent';
            }
            this.applyAiCounselorPermission(res);
            this.applyAchievementsPermission(res);
            this.applyPackageBundlePermission(res);
            this.refreshShellForUrl(this.router.url);
            this.tryPromptCdse();
            this.fetchCreditBalance(res.role);
            this.activityTracker.start(res.role);
          } else {
            this.activityTracker.stop();
            localStorage.removeItem(environment.storageKey);
            localStorage.removeItem('currentRole');
            localStorage.removeItem('timezone');
            this.router.navigate(['/auth/login']);
          }
        } else {
          if (localStorage.getItem(environment.storageKey)) {
            this.getProfile();
          }
        }
      });
    this.authService.permissionUpdate
      .pipe(takeUntil(this.destroy))
      .subscribe((res: any) => {
        if (res === null) return;
        if (this.profile?.role === UserRole.STUDENT) {
          this.canAccessAiCounselor = res.student?.myCounselor?.canAccessAiCounselor ?? false;
          this.canAccessAchievements =
            (res.student?.pathwayFeatures?.canAccessAiAchievements ?? false) ||
            (res.student?.pathwayFeatures?.canAccessAchievements ?? false);
          this.canAccessPackageBundle = res.student?.pathwayFeatures?.canAccessPackageBundle ?? false;
        } else if (this.profile?.role === UserRole.TUTOR || this.profile?.role === UserRole.COUNSELOR) {
          this.canAccessAiCounselor = res.instructor?.aiTutor?.canAccessAiCounselor ?? false;
        }
      });
    this.authService.isOpen$
      .pipe(takeUntil(this.destroy))
      .subscribe((isOpen: boolean) => {
        this.isMobileSidebarOpen = isOpen;
      });
    this.initializeSocket();
    this.getNotificationCount();
    this.notiReceived();
    const fcmToken = localStorage.getItem('fcmToken');
    this.authService.deviceTokenSubject.next(fcmToken);

    this.webLogo = this.getWebSettings()?.webLogo || this.assets.logo;
    setTimeout(() => {
      const updatedLogo = this.getWebSettings()?.webLogo;
      if (updatedLogo) {
        this.webLogo = updatedLogo;
        this.cdr.detectChanges();
      }
    }, 500);
  }

  private getWebSettings(): { webLogo?: string } & Record<string, unknown> {
    const val = localStorage.getItem('webSettings');
    if (!val || val === 'undefined') return {};
    try {
      return JSON.parse(val) as { webLogo?: string } & Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private initializeSocket(): void {
    this._socketService.initSocket();
  }

  ngAfterViewInit(): void {
    this.permissionsUpdate();
  }

  getProfile(): void {
    this._spinnerService.addToLoader('getProfile');
    this.authService
      .getProfile()
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: UserResponse) => {
          this._spinnerService.removeFromLoader('getProfile');
          if (res.role !== UserRole.ADMIN) {
            this.profile = res;
            this.setSw(this.profile.role);
            if (this.profile.role === UserRole.TUTOR || this.profile.role === UserRole.COUNSELOR) {
              this.currentPath = 'tutorCounselor';
            } else {
              this.currentPath = 'studentParent';
            }
            const timeZoneToStore = res.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            localStorage.setItem('timezone', timeZoneToStore);
            this.applyAiCounselorPermission(res);
            this.applyAchievementsPermission(res);
            this.applyPackageBundlePermission(res);
            this.refreshShellForUrl(this.router.url);
            this.authService.profileDetailsSubject.next(res);
          } else {
            this.authService.stopRefreshTimer();
            localStorage.clear();
            this.router.navigate(['/auth/login']);
          }
        },
        error: () => {
          this._spinnerService.removeFromLoader('getProfile');
        }
      });
  }

  setSw(role: number): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    navigator.serviceWorker.ready.then((registration) => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller?.postMessage({
          type: 'role',
          roleData: role
        });
      }
    });
  }

  handleRouteNavigation(): void {
    if (this.profile?.role === UserRole.STUDENT || this.profile?.role === UserRole.PARENT) {
      this.router.navigate(['/my-profile']);
    } else if (this.profile?.role === UserRole.TUTOR || this.profile?.role === UserRole.COUNSELOR) {
      this.router.navigate(['/tutor/profile']);
    }
  }

  toggleSidenav(): void {
    this.authService.toggleSidebarView();
  }

  /**
   * Bound to the drawer's `opened`. `mat-drawer-opened` is what the sidebar CSS keys off, and it means
   * two different things by breakpoint (see `styles.scss`): the collapsed 90px icon rail on desktop,
   * and the visible overlay on small screens. So desktop stays "collapsed unless hovered", while small
   * screens keep the hamburger/close-button behaviour.
   */
  protected get isSidenavCollapsed(): boolean {
    return this.isHoverSidebar() ? !this.isSidebarHovered : this.isMobileSidebarOpen;
  }

  /** Expands the rail on pointer enter and collapses it on leave; a no-op on the overlay breakpoints. */
  protected onSidebarHover(hovered: boolean): void {
    if (!this.isHoverSidebar()) {
      return;
    }
    this.isSidebarHovered = hovered;
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    // Dropping below the breakpoint mid-hover would otherwise leave the overlay stuck open.
    if (!this.isHoverSidebar()) {
      this.isSidebarHovered = false;
    }
  }

  private isHoverSidebar(): boolean {
    return window.innerWidth >= MainComponent.SIDEBAR_HOVER_MIN_WIDTH;
  }

  /** i18n key for role line under the name in the v2 teal header. */
  protected profileRoleLabelKey(): string {
    if (!this.profile) {
      return '';
    }
    if (this.profile.role === UserRole.STUDENT) {
      return 'Student';
    }
    if (this.profile.role === UserRole.PARENT) {
      return 'Parent';
    }
    return '';
  }

  private refreshShellForUrl(url: string): void {
    const path = url.split('?')[0];
    this.isAiCounselorPage = path === '/ai-counselor' || path === '/tutor/ai-counselor';
  }

  /** Gates the chatbot FAB/copilot panel for STUDENT (student: My Counselor) and
   *  TUTOR/COUNSELOR (admin: Instructor Permissions → AI Tutor) alike. */
  private applyAiCounselorPermission(profile: UserResponse): void {
    if (profile.role === UserRole.STUDENT) {
      this.canAccessAiCounselor =
        profile.userPermissions?.student?.myCounselor?.canAccessAiCounselor ?? false;
    } else if (profile.role === UserRole.TUTOR || profile.role === UserRole.COUNSELOR) {
      this.canAccessAiCounselor =
        profile.instructorPermissions?.instructor.aiTutor?.canAccessAiCounselor ?? false;
    }
  }

  /** STUDENT only — the header level badge has no tutor/parent equivalent. */
  private applyAchievementsPermission(profile: UserResponse): void {
    if (profile.role !== UserRole.STUDENT) {
      return;
    }
    const bag = profile.userPermissions?.student?.pathwayFeatures;
    this.canAccessAchievements = (bag?.canAccessAiAchievements ?? false) || (bag?.canAccessAchievements ?? false);
  }

  /** STUDENT only — the header "Upgrade to Premium" button has no tutor/parent equivalent. */
  private applyPackageBundlePermission(profile: UserResponse): void {
    if (profile.role !== UserRole.STUDENT) {
      return;
    }
    this.canAccessPackageBundle = profile.userPermissions?.student?.pathwayFeatures?.canAccessPackageBundle ?? false;
  }

  toggleCopilot(): void {
    this.isCopilotOpen = !this.isCopilotOpen;
  }

  onCopilotClosed(): void {
    this.isCopilotOpen = false;
  }

  onImageError(event: any): void {
    (event.target as HTMLImageElement).src =  assets.userPlaceholderLogo;
  }

  private permissionsUpdate(): void {
    this._socketService.onEvent('permissionsUpdatedOk').subscribe({
      next: (res: any) => { 
        this.authService.permissionUpdate.next(res.data);
      }
    });
  }

  protected getNotificationCount(): void {
    this.authService
      .getNotificationCount()
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: dashboardNotification) => {
          this.notificationCount = res.receiverNotificationCount;
        }
      });
  }

  /** `/tutor/credit-packages/packages` for tutor/counsellor, `/credit-packages/packages` for student. */
  protected get creditPackagesRoute(): string {
    return this.profile?.role === UserRole.TUTOR || this.profile?.role === UserRole.COUNSELOR
      ? '/tutor/credit-packages/packages'
      : '/credit-packages/packages';
  }

  protected get notificationRoute(): string {
    return this.profile?.role === UserRole.TUTOR || this.profile?.role === UserRole.COUNSELOR
      ? '/tutor/manage-notification'
      : '/manage-notification/notification';
  }

  /** Credit balance endpoint is only available to STUDENT, TUTOR, and COUNSELOR (not PARENT/ADMIN). */
  private fetchCreditBalance(role: number): void {
    if (role !== UserRole.STUDENT && role !== UserRole.TUTOR && role !== UserRole.COUNSELOR) {
      this.authService.creditBalanceSubject.next(null);
      return;
    }
    this.authService
      .getCreditBalance()
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: CreditBalanceResponse) => this.authService.creditBalanceSubject.next(res)
      });
  }

  /** Opens the guided Upgrade to Premium dialog in place, instead of navigating to Manage Package. */
  protected goToPurchasePremium(): void {
    this.dialog.open(PremiumUpgradeWizardComponent, {
      width: '600px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  protected onNotificationClick(): void {
    this.notificationCount = 0;
    this.authService.notificationClicked.next(true);
    this.isNotificationDrawerOpen = true;
  }

  protected onNotificationDrawerClosed(): void {
    this.isNotificationDrawerOpen = false;
  }

  private notiReceived(): void {
    this._socketService.onEvent('noti_received').subscribe({
      next: (res: any) => {
        this.notificationCount = res.data;
      }
    });
  }

  ngOnDestroy(): void {
    this.activityTracker.stop();
    this.destroy.complete();
    this.destroy.unsubscribe();
  }
}
